import { useState, useEffect, useRef } from 'react';
import { accessApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../hooks/use-toast';
import {
  Database, Upload, Trash2, Table as TableIcon, Loader2,
  HardDrive, RefreshCw, FileSpreadsheet,
} from 'lucide-react';
import { ImportCard } from './access/ImportCard';
import { AccessTableViewer } from './access/AccessTableViewer';
import { AccessTablesList } from './access/AccessTablesList';

export const AccessDatabase = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importingHH, setImportingHH] = useState(false);
  const [importingVentas, setImportingVentas] = useState(false);
  const [importingActivo, setImportingActivo] = useState(false);
  const [importingCompras, setImportingCompras] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importHHResult, setImportHHResult] = useState(null);
  const [importVentasResult, setImportVentasResult] = useState(null);
  const [importActivoResult, setImportActivoResult] = useState(null);
  const [importComprasResult, setImportComprasResult] = useState(null);
  const [importingTodo, setImportingTodo] = useState(false);
  const [importTodoResult, setImportTodoResult] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [tables, setTables] = useState([]);
  const [tablesError, setTablesError] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowLimit, setRowLimit] = useState(100);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAccessInfo();
  }, []);

  const loadAccessInfo = async () => {
    setLoading(true);
    setTablesError(false);
    try {
      const res = await accessApi.getInfo();
      setDbInfo(res.data);
      try {
        const tablesRes = await accessApi.getTables();
        setTables(tablesRes.data.tables || []);
        setTablesError(false);
      } catch {
        setTables([]);
        setTablesError(true);
      }
    } catch (error) {
      if (error.response?.status !== 404) {

      }
      setDbInfo(null);
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRepairTools = async () => {
    setRepairing(true);
    try {
      const res = await accessApi.repairTools();
      toast({ title: res.data.message });
      await loadAccessInfo();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error al reparar herramientas', variant: 'destructive' });
    } finally {
      setRepairing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mdb') && !file.name.toLowerCase().endsWith('.accdb')) {
      toast({ title: 'El archivo debe ser .mdb o .accdb', variant: 'destructive' });
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      };
      const res = await accessApi.upload(file, onProgress);
      toast({ title: res.data.message });
      loadAccessInfo();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error al subir el archivo', variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async () => {
    if (!window.confirm('Estas seguro de eliminar el archivo Access?')) return;
    try {
      await accessApi.deleteFile();
      toast({ title: 'Archivo eliminado' });
      setDbInfo(null);
      setTables([]);
      setSelectedTable(null);
      setTableData(null);
      setImportResult(null);
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  // Shared polling function for background imports
  const pollImportStatus = (setLoadingFn, setResultFn, label) => {
    let pollCount = 0;
    const interval = setInterval(async () => {
      pollCount++;
      try {
        const statusRes = await accessApi.getImportStatus();
        const st = statusRes.data;
        if (!st.running) {
          clearInterval(interval);
          setLoadingFn(false);
          if (st.error) {
            toast({ title: `Error: ${st.error}`, variant: 'destructive' });
          } else if (st.completed && st.results) {
            setResultFn(st.results);
            toast({ title: `${label} completada` });
          }
        }
      } catch {
        if (pollCount > 3) {
          clearInterval(interval);
          setLoadingFn(false);
          toast({ title: 'Error al verificar estado de importacion', variant: 'destructive' });
        }
      }
    }, 3000);
  };

  const handleResetImportStatus = async () => {
    try {
      await accessApi.resetImportStatus();
      setImporting(false);
      setImportingHH(false);
      setImportingVentas(false);
      setImportingActivo(false);
      setImportingCompras(false);
      setImportingTodo(false);
      toast({ title: 'Estado de importacion reseteado' });
    } catch {
      toast({ title: 'Error al resetear estado', variant: 'destructive' });
    }
  };

  const createImportHandler = (confirmMsg, setLoadingFn, setResultFn, apiFn, label) => async () => {
    if (!window.confirm(confirmMsg)) return;
    setLoadingFn(true);
    setResultFn(null);
    try {
      await apiFn();
      pollImportStatus(setLoadingFn, setResultFn, label);
    } catch (error) {
      toast({ title: error.response?.data?.detail || `Error en ${label}`, variant: 'destructive' });
      setLoadingFn(false);
    }
  };

  const handleImportTodo = createImportHandler(
    'Importar TODOS los datos desde Access?\n\nIncluye: Clientes, Proyectos, OTs, Trabajadores, HH, Ventas, Activo Circulante, Compras, Materiales y Precios.\n\nPuede tomar varios minutos.',
    setImportingTodo, setImportTodoResult, accessApi.importTodo, 'Importacion Completa'
  );
  const handleImportData = createImportHandler(
    'Importar Clientes, Proyectos y OTs desde Access?',
    setImporting, setImportResult, accessApi.importData, 'Importacion'
  );
  const handleImportHH = createImportHandler(
    'Importar Trabajadores y Control de Horas Hombre desde Access?',
    setImportingHH, setImportHHResult, accessApi.importHH, 'Importacion HH'
  );
  const handleImportVentas = createImportHandler(
    'Importar Ventas/Proformas desde Access?',
    setImportingVentas, setImportVentasResult, accessApi.importVentas, 'Importacion Ventas'
  );
  const handleImportActivoCirculante = createImportHandler(
    'Importar Activo Circulante desde Access?',
    setImportingActivo, setImportActivoResult, accessApi.importActivoCirculante, 'Importacion Activo Circulante'
  );
  const handleImportCompras = createImportHandler(
    'Importar Compras (OC, Pedidos, Recepciones) desde Access?',
    setImportingCompras, setImportComprasResult, accessApi.importCompras, 'Importacion Compras'
  );

  const handleViewTable = async (tableName) => {
    setSelectedTable(tableName);
    setLoadingTable(true);
    setTableData(null);
    try {
      const res = await accessApi.getTableData(tableName, rowLimit);
      setTableData(res.data);
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error al cargar la tabla', variant: 'destructive' });
    } finally {
      setLoadingTable(false);
    }
  };

  const filteredTables = tables.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="access-database-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Database className="w-8 h-8 text-primary" />
            Base de Datos Access
          </h1>
          <p className="text-muted-foreground">
            Conexion con tu base de datos historica
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRepairTools}
          disabled={repairing}
          className="border-orange-500 text-orange-600 hover:bg-orange-50"
          data-testid="repair-tools-btn"
        >
          {repairing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Actualizando...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" /> Actualizar Tablas</>
          )}
        </Button>
      </div>

      {/* Upload Section */}
      {!dbInfo && (
        <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
          <CardContent className="p-8 text-center">
            <HardDrive className="w-16 h-16 mx-auto mb-4 text-primary/50" />
            <h3 className="font-heading text-xl font-semibold mb-2">No hay archivo Access cargado</h3>
            <p className="text-muted-foreground mb-6">Sube tu archivo .mdb o .accdb para comenzar a consultar tus datos historicos</p>
            <input ref={fileInputRef} type="file" accept=".mdb,.accdb" onChange={handleFileUpload} className="hidden" id="access-file-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="lg" data-testid="upload-access-btn">
              {uploading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subiendo... {uploadProgress}%</>
              ) : (
                <><Upload className="w-5 h-5 mr-2" /> Subir Archivo Access</>
              )}
            </Button>
            {uploading && (
              <div className="w-full max-w-md mx-auto mt-4">
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-sm text-muted-foreground text-center mt-2">Subiendo archivo grande, por favor espera...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Database Info + Import + Tables */}
      {dbInfo && (
        <>
          {/* DB Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-primary" />
                    {dbInfo.filename}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Subido: {new Date(dbInfo.upload_date).toLocaleString('es-CL')}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <input ref={fileInputRef} type="file" accept=".mdb,.accdb" onChange={handleFileUpload} className="hidden" id="access-file-input-replace" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="replace-access-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Reemplazar
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeleteFile} className="text-destructive hover:text-destructive" data-testid="delete-access-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 text-sm">
                <Badge variant="secondary" className="py-1"><HardDrive className="w-3 h-3 mr-1" />{dbInfo.file_size_mb} MB</Badge>
                <Badge variant="secondary" className="py-1"><TableIcon className="w-3 h-3 mr-1" />{tables.length} tablas</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Import All */}
          <ImportCard
            title="Importar Todo desde Access"
            description="Un solo click para importar todos los datos: Clientes, Proyectos, OTs, Trabajadores, HH, Ventas, Activo Circulante, Compras, Materiales y Precios."
            colorClass="indigo"
            importing={importingTodo}
            onImport={handleImportTodo}
            result={importTodoResult}
            buttonLabel="Importar Todo"
            loadingLabel="Importando todo... (puede tomar varios minutos)"
            showReset
            onResetImport={handleResetImportStatus}
          />

          {/* Import Datos */}
          <ImportCard
            title="Importar Datos a Nueva Base de Datos"
            description="Importa Clientes, Proyectos y Ordenes de Trabajo desde Access"
            colorClass="green"
            importing={importing}
            onImport={handleImportData}
            result={importResult}
            buttonLabel="Importar Datos"
            loadingLabel="Importando datos..."
            resultCols={3}
            resultFields={[
              { label: 'Clientes', value: (r) => r.clientes?.imported || 0 },
              { label: 'Proyectos', value: (r) => r.proyectos?.imported || 0 },
              { label: 'Ordenes', value: (r) => r.ordenes_trabajo?.imported || 0 },
            ]}
            details={
              <div>
                <p className="mb-2">Se importaran las siguientes tablas:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Clientes 1</strong> - Clientes</li>
                  <li><strong>proyecto</strong> - Proyectos (relacionados con clientes)</li>
                  <li><strong>ot</strong> - Ordenes de Trabajo (relacionadas con proyectos)</li>
                </ul>
              </div>
            }
          />

          {/* Import HH */}
          <ImportCard
            title="Importar Horas Hombre (HH)"
            description="Importa Trabajadores y Control de Horas desde Access"
            colorClass="blue"
            importing={importingHH}
            onImport={handleImportHH}
            result={importHHResult}
            buttonLabel="Importar Trabajadores y HH"
            loadingLabel="Importando HH... (puede tomar varios minutos)"
            resultCols={2}
            resultFields={[
              { label: 'Trabajadores', value: (r) => r.trabajadores?.imported || 0 },
              { label: 'Registros HH', value: (r) => r.control_hh?.imported?.toLocaleString() || 0 },
            ]}
            details={
              <div>
                <p className="mb-2">Se importaran las siguientes tablas:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Trabajadores</strong> - 627 trabajadores</li>
                  <li><strong>Control HH</strong> - ~64,000 registros de horas (relacionados a OTs y trabajadores)</li>
                </ul>
              </div>
            }
          />

          {/* Import Ventas */}
          <ImportCard
            title="Importar Ventas / Proformas"
            description="Importa Facturas, Proformas y su detalle para ver facturacion por OT/Proyecto"
            colorClass="purple"
            importing={importingVentas}
            onImport={handleImportVentas}
            result={importVentasResult}
            buttonLabel="Importar Ventas y Detalle"
            loadingLabel="Importando Ventas..."
            resultCols={2}
            resultFields={[
              { label: 'Ventas/Facturas', value: (r) => (r.ventas?.imported || 0) + (r.ventas?.updated || 0) },
              { label: 'Lineas Detalle', value: (r) => r.ventas_detalle?.imported?.toLocaleString() || 0 },
            ]}
            details={
              <div>
                <p className="mb-2">Se importaran las siguientes tablas:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Ventas 1</strong> - ~2,515 facturas/proformas (cabecera)</li>
                  <li><strong>Ventas Detalle</strong> - ~13,900 lineas de detalle</li>
                </ul>
                <p className="mt-2 text-xs">Permite ver facturacion por OT y Proyecto, buscar proformas con su detalle.</p>
              </div>
            }
          />

          {/* Import Activo Circulante */}
          <ImportCard
            title="Importar Activo Circulante"
            description="Importa datos de COSTOCAJA para el informe de Activo Circulante"
            colorClass="teal"
            importing={importingActivo}
            onImport={handleImportActivoCirculante}
            result={importActivoResult}
            buttonLabel="Importar Activo Circulante"
            loadingLabel="Importando..."
            resultCols={2}
            resultFields={[
              { label: 'Nuevos', value: (r) => r.results?.imported || 0 },
              { label: 'Actualizados', value: (r) => r.results?.updated || 0 },
            ]}
            details={
              <div>
                <p className="mb-2">Se importara la tabla <strong>COSTOCAJA</strong> con:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Monto presupuestado por OT</li>
                  <li>Total facturado</li>
                  <li>Saldo y/o Retencion</li>
                  <li>Avance Financiero %</li>
                  <li>Por Facturar Ganado</li>
                  <li>Avance Fisico %</li>
                </ul>
              </div>
            }
          />

          {/* Import Compras */}
          <ImportCard
            title="Importar Modulo de Compras"
            description="Importa OC, Pedidos, Recepciones y sus detalles"
            colorClass="purple"
            importing={importingCompras}
            onImport={handleImportCompras}
            result={importComprasResult}
            buttonLabel="Importar Modulo de Compras"
            loadingLabel="Importando Compras..."
            resultCols={3}
            resultFields={[
              { label: 'OC', value: (r) => (r.results?.ordenes_compra?.imported || 0) + (r.results?.ordenes_compra?.updated || 0) },
              { label: 'Pedidos', value: (r) => (r.results?.pedidos?.imported || 0) + (r.results?.pedidos?.updated || 0) },
              { label: 'Recepciones', value: (r) => (r.results?.recepciones?.imported || 0) + (r.results?.recepciones?.updated || 0) },
            ]}
            extraResult={importComprasResult && (
              <p className="text-center mt-2 text-sm font-medium">
                Total: {importComprasResult.total_registros} registros
              </p>
            )}
            details={
              <div>
                <p className="mb-2">Se importaran las siguientes tablas:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>orden_compra</strong> - Ordenes de Compra (solo aprobadas para calculos)</li>
                  <li><strong>detalle_orden</strong> - Detalle de cada OC</li>
                  <li><strong>pedido</strong> - Requisiciones/Pedidos</li>
                  <li><strong>pedido 1</strong> - Detalle de requisiciones</li>
                  <li><strong>recepcion</strong> - Recepciones de materiales</li>
                  <li><strong>detalle_recepcion</strong> - Detalle de recepciones</li>
                </ul>
              </div>
            }
          />

          {/* Table Viewer */}
          {selectedTable && (
            <AccessTableViewer
              selectedTable={selectedTable}
              tableData={tableData}
              loadingTable={loadingTable}
              onClose={() => { setSelectedTable(null); setTableData(null); }}
            />
          )}

          {/* Tables List */}
          {!selectedTable && (
            <AccessTablesList
              filteredTables={filteredTables}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              rowLimit={rowLimit}
              setRowLimit={setRowLimit}
              tablesError={tablesError}
              repairing={repairing}
              onRepair={handleRepairTools}
              onViewTable={handleViewTable}
            />
          )}
        </>
      )}

      {/* Help Section */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">Como usar esta funcion?</h4>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Sube tu archivo Access (.mdb o .accdb)</li>
            <li>Explora las tablas disponibles</li>
            <li>Selecciona una tabla para ver sus datos</li>
            <li>Indica que informacion necesitas importar al nuevo sistema</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};
